// Collectivité choisie par l'usager pour le portail : mémorisée sur l'appareil (le compte est
// national, chaque démarche vise une collectivité, H7-01) et lue depuis l'adresse /citoyen/:code.
import { ecrireStockage, lireStockage } from '@cc/ui';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useSyncExternalStore } from 'react';
import { requete } from '../../api/client';
import type { CollectivitePublique, Liste } from './types';

const CLE = 'cc-citoyen:collectivite';
const ecouteurs = new Set<() => void>();

export interface CollectiviteChoisie {
  code: string;
  nom: string;
}

function lire(): CollectiviteChoisie | null {
  const v = lireStockage<CollectiviteChoisie>(CLE);
  return v && typeof v.code === 'string' && typeof v.nom === 'string' ? v : null;
}

let courante: CollectiviteChoisie | null = lire();

export function choisirCollectivite(c: CollectiviteChoisie | null): void {
  courante = c;
  if (c) ecrireStockage(CLE, c);
  else ecrireStockage(CLE, null);
  for (const e of ecouteurs) e();
}

function abonner(e: () => void) {
  ecouteurs.add(e);
  return () => {
    ecouteurs.delete(e);
  };
}

/** Collectivité mémorisée et fonction de choix. */
export function useCollectiviteChoisie(): [CollectiviteChoisie | null, (c: CollectiviteChoisie | null) => void] {
  const valeur = useSyncExternalStore(abonner, () => courante, () => null);
  const choisir = useCallback((c: CollectiviteChoisie | null) => choisirCollectivite(c), []);
  return [valeur, choisir];
}

/** Collectivités actives ouvertes au portail (GET /public/collectivites, sans compte). */
export function useCollectivitesPubliques() {
  return useQuery({
    queryKey: ['citoyen', 'collectivites'],
    queryFn: ({ signal }) => requete<Liste<CollectivitePublique>>('/public/collectivites', { signal }).then((r) => r.elements),
    staleTime: 5 * 60_000,
  });
}

export const LIBELLES_TYPE_COLLECTIVITE: Record<string, string> = {
  commune: 'Commune',
  conseil_departemental: 'Conseil départemental',
};

/** Code de collectivité valide dans une adresse (2 à 8 lettres ou chiffres, majuscules). */
export function codeCollectiviteValide(code: string | undefined): string | null {
  if (!code) return null;
  const c = code.trim().toUpperCase();
  return /^[A-Z0-9]{2,8}$/.test(c) ? c : null;
}
